// src/SpeciesSpotter.js
import React, { useState, useRef } from 'react';
import JSZip from 'jszip';

const MAX_PER_RUN = 200;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Retry helper for rare 429/5xx
async function postWithRetry(formData, tries = 2) {
  let lastErr;
  for (let i = 0; i <= tries; i++) {
    try {
      const res = await fetch('/api/classify?debug=0', { method: 'POST', body: formData });
      if (res.ok) return res;
      if (res.status === 429 || res.status >= 500) {
        const body = await res.text().catch(() => '');
        lastErr = new Error(`HTTP ${res.status}: ${body || res.statusText}`);
      } else {
        return res;
      }
    } catch (e) { lastErr = e; }
    await sleep(600 * (i + 1));
  }
  throw lastErr || new Error('Request failed');
}

const SpeciesSpotter = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [sortingStatus, setSortingStatus] = useState('');
  const [results, setResults] = useState([]);
  const [buildingZip, setBuildingZip] = useState(false);
  const [readingZip, setReadingZip] = useState(false);
  const [isSorting, setIsSorting] = useState(false);

  const zipInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const isImageName = (name) => /\.(jpg|jpeg|png|webp)$/i.test(name || '');
  const resetInputs = () => {
    if (zipInputRef.current) zipInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  async function resizeImage(file, maxDim = 1280, quality = 0.9) {
    const img = document.createElement('img');
    const url = URL.createObjectURL(file);
    try {
      await new Promise((resolve, reject) => {
        img.onload = resolve; img.onerror = reject; img.src = url;
      });
      const { naturalWidth: w, naturalHeight: h } = img;
      if (!w || !h) throw new Error('decode failed');
      const scale = Math.min(1, maxDim / Math.max(w, h));
      if (scale >= 1) return new File([file], file.name, { type: 'image/jpeg' });
      const nw = Math.round(w * scale), nh = Math.round(h * scale);
      const canvas = document.createElement('canvas');
      canvas.width = nw; canvas.height = nh;
      const ctx = canvas.getContext('2d', { alpha: false });
      ctx.drawImage(img, 0, 0, nw, nh);
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
      return new File([blob], file.name, { type: 'image/jpeg' });
    } catch { return file; }
    finally { URL.revokeObjectURL(url); }
  }

  async function handleZipUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setReadingZip(true);
    setSortingStatus('Reading zip...');
    try {
      const zip = await JSZip.loadAsync(file);
      const picked = [];
      const entries = Object.keys(zip.files);
      await Promise.all(entries.map(async (path) => {
        const entry = zip.files[path];
        if (entry.dir) return;
        if (!isImageName(path)) return;
        const blob = await entry.async('blob');
        const name = path.split('/').pop() || path;
        picked.push(new File([blob], name, { type: blob.type || 'image/jpeg' }));
      }));
      setUploadedFiles(picked);
      setResults([]);
      setSortingStatus(picked.length
        ? `${picked.length} images ready. Click "Start Sorting".`
        : 'No images found in the zip.');
    } catch (err) {
      console.error(err);
      setSortingStatus('Error reading zip file.');
    } finally {
      setReadingZip(false);
      resetInputs();
    }
  }

  function handleFolderUpload(e) {
    const files = Array.from(e.target.files || []);
    const picked = files.filter((f) => isImageName(f.name));
    setUploadedFiles(picked);
    setResults([]);
    setSortingStatus(picked.length
      ? `${picked.length} images ready. Click "Start Sorting".`
      : 'No images found in the selected folder.');
  }

  async function startSorting() {
    if (!uploadedFiles.length || isSorting) return;
    const files = uploadedFiles.slice(0, MAX_PER_RUN);
    setIsSorting(true);
    setResults([]);
    setSortingStatus(`Preparing ${files.length} images...`);

    const collected = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const original = files[i];
        setSortingStatus(`(${i + 1}/${files.length}) Resizing ${original.name}...`);
        const img = await resizeImage(original);

        const form = new FormData();
        form.append('images', img, img.name);

        setSortingStatus(`(${i + 1}/${files.length}) Classifying ${original.name}...`);
        let json;
        try {
          const res = await postWithRetry(form, 2);
          json = await res.json();
        } catch (e) {
          collected.push({ name: original.name, label: 'error', confidence: 0, specifics: [], error: e.message || String(e) });
          setResults([...collected]);
          continue;
        }

        const item = (json?.results && json.results[0]) || {
          name: original.name, label: 'error', confidence: 0, specifics: [], error: 'No result'
        };
        // ensure original filename
        item.name = original.name;

        // Client-side safety net: if API specifics missing but macro is meaningful, add a default.
        if ((!item.specifics || !item.specifics.length) && item.label && !['empty','other','error','too_large'].includes(item.label)) {
          item.specifics = [ item.label === 'human' ? 'person' : item.label ];
        }

        collected.push(item);
        setResults([...collected]);

        setSortingStatus(
          `(${i + 1}/${files.length}) ${original.name} → ${item.label}` +
          (typeof item.confidence === 'number' ? ` (${(item.confidence * 100).toFixed(0)}%)` : '')
        );

        await sleep(150);
      }
      setSortingStatus(`Done. Classified ${collected.length} images.`);
    } catch (err) {
      console.error(err);
      setSortingStatus('Error during classification.');
    } finally {
      setIsSorting(false);
    }
  }

  async function downloadSortedZip() {
    if (!results.length) return;
    setBuildingZip(true);
    setSortingStatus('Packing sorted zip...');
    try {
      const byName = new Map(uploadedFiles.map((f) => [f.name, f]));
      const zip = new JSZip();
      results.forEach(({ name, label }) => {
        const file = byName.get(name);
        if (!file) return;
        const folder = (label || 'other').toString();
        zip.folder(folder)?.file(file.name, file);
      });
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sorted-photos.zip';
      a.click();
      URL.revokeObjectURL(url);
      setSortingStatus('Sorted zip downloaded.');
    } catch (err) {
      console.error(err);
      setSortingStatus('Error building sorted zip.');
    } finally {
      setBuildingZip(false);
    }
  }

  return (
    <div style={{ padding: '20px', position: 'relative' }}>
      <h1>SpeciesSpotter</h1>
      <p>
        Upload a <strong>ZIP</strong> or a <strong>folder</strong> of wildlife pictures. We’ll label
        them on the server and let you download a sorted ZIP with folders like “deer”, “human”, or “empty”.
      </p>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <label><strong>Upload ZIP (works in all browsers)</strong></label><br />
          <input ref={zipInputRef} type="file" accept=".zip" onChange={handleZipUpload} />
        </div>

        <div>
          <label>
            <strong>Upload Folder</strong>{' '}
            <small>(best in Chrome/Edge; Firefox users can use ZIP)</small>
          </label><br />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            webkitdirectory=""
            onChange={handleFolderUpload}
          />
        </div>
      </div>

      {(readingZip || uploadedFiles.length > 0) && (
        <div style={{ marginTop: '20px' }}>
          <h2>Uploaded Files:</h2>
          {readingZip ? (
            <p>Reading zip…</p>
          ) : (
            <ul style={{ maxHeight: 240, overflow: 'auto', paddingRight: 8 }}>
              {uploadedFiles.map((file, index) => (
                <li key={`${file.name}-${index}`}>{file.name}</li>
              ))}
            </ul>
          )}

          <button
            onClick={startSorting}
            disabled={readingZip || uploadedFiles.length === 0 || isSorting}
            style={{
              backgroundColor: isSorting ? '#6c757d' : '#007bff',
              color: 'white',
              padding: '10px 15px',
              border: 'none',
              borderRadius: '5px',
              cursor: (readingZip || uploadedFiles.length === 0) ? 'not-allowed' : 'pointer',
              marginTop: '10px'
            }}
          >
            {isSorting ? 'Working…' : 'Start Sorting'}
          </button>
          <p style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{sortingStatus}</p>
        </div>
      )}

      {results.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3>Results</h3>
          <ul>
            {results.map((r, idx) => (
              <li key={`${r.name}-${idx}`}>
                {r.name} → <strong>{r.label}</strong>
                {typeof r.confidence === 'number' ? ` (${(r.confidence * 100).toFixed(1)}%)` : ''}
                {r.error ? <> — {r.error}</> : null}
                {r.specifics && r.specifics.length > 0 ? (
                  <div style={{ color: '#555' }}>Details: {r.specifics.join(', ')}</div>
                ) : null}
              </li>
            ))}
          </ul>

          <button
            onClick={downloadSortedZip}
            disabled={buildingZip}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              padding: '10px 15px',
              border: 'none',
              borderRadius: '6px'
            }}
          >
            {buildingZip ? 'Building…' : 'Download Sorted ZIP'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SpeciesSpotter;
